import React, { useState } from "react";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db, functions } from "../firebase";
import { httpsCallable } from "firebase/functions";
import logo from "../assets/river-ranch-logo.png";
import { Link } from "react-router-dom";
import hero from "../assets/hero-poker.jpg";

const AGREEMENT_VERSION = "1.0";
const SMS_DISCLOSURE_VERSION = "2.0";
const SMS_OPT_IN_DISCLOSURE =
  "By checking this box, I agree to receive recurring automated text messages from River Ranch Poker Club regarding tournament announcements, event reminders, registration confirmations, and membership account communications. Message frequency varies. Message and data rates may apply. Reply STOP to opt out. Reply HELP for help. Consent is not a condition of membership or purchase.";

function normalizePhone(value) {
  return String(value || "").replace(/\D/g, "");
}

function formatPhoneInput(value) {
  const digits = normalizePhone(value).slice(0, 10);

  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

export default function Apply() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [dob, setDob] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [underage, setUnderage] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [agreementAccepted, setAgreementAccepted] = useState(false);
  const [smsOptIn, setSmsOptIn] = useState(false);

  const checkDuplicateData = httpsCallable(functions, "checkDuplicateApplicationData");

  const calculateAge = (birthDate) => {
    const today = new Date();
    const dobDate = new Date(birthDate);
    let age = today.getFullYear() - dobDate.getFullYear();
    const m = today.getMonth() - dobDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < dobDate.getDate())) age--;
    return age;
  };

  const handleDobChange = (e) => {
    const value = e.target.value;
    setDob(value);
    setUnderage(value && calculateAge(value) < 21);
  };

  const runDuplicateCheck = async (nextEmail, nextPhone) => {
    const normalizedEmail = String(nextEmail || "").trim().toLowerCase();
    const normalizedPhone = normalizePhone(nextPhone);

    if (!normalizedEmail && !normalizedPhone) {
      setEmailError("");
      setPhoneError("");
      return;
    }

    try {
      const result = await checkDuplicateData({
        email: normalizedEmail,
        phone: normalizedPhone,
      });

      setEmailError(result.data?.emailDuplicate ? result.data.emailMessage : "");
      setPhoneError(result.data?.phoneDuplicate ? result.data.phoneMessage : "");
    } catch (error) {
      console.error("Error checking duplicates:", error);

      if (normalizedEmail) {
        setEmailError("Unable to verify application details at this time.");
      }
      if (normalizedPhone) {
        setPhoneError("Unable to verify phone number at this time.");
      }
    }
  };

  const handleEmailChange = async (e) => {
    const value = e.target.value;
    setEmail(value);
    setEmailError("");
    await runDuplicateCheck(value, phone);
  };

  const handlePhoneChange = async (e) => {
    const formatted = formatPhoneInput(e.target.value);
    setPhone(formatted);
    setPhoneError("");

    const normalizedPhone = normalizePhone(formatted);
    if (!normalizedPhone) return;

    await runDuplicateCheck(email, formatted);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const normalizedEmail = email.trim().toLowerCase();
    const normalizedPhone = normalizePhone(phone);

    if (underage || emailError || phoneError || !agreementAccepted) return;

    if (normalizedPhone.length !== 10) {
      setPhoneError("Please enter a valid 10-digit phone number.");
      return;
    }

    setLoading(true);

    try {
      const result = await checkDuplicateData({
        email: normalizedEmail,
        phone: normalizedPhone,
      });

      if (result.data?.emailDuplicate || result.data?.phoneDuplicate) {
        setEmailError(result.data?.emailDuplicate ? result.data.emailMessage : "");
        setPhoneError(result.data?.phoneDuplicate ? result.data.phoneMessage : "");
        setLoading(false);
        return;
      }

      await addDoc(collection(db, "applications"), {
        name: name.trim(),
        email: normalizedEmail,
        phone: normalizedPhone,
        dob,
        status: "pending",

        agreementAccepted: true,
        agreementAcceptedAt: serverTimestamp(),
        agreementVersion: AGREEMENT_VERSION,

        smsOptIn: !!smsOptIn,
        smsOptInAt: smsOptIn ? serverTimestamp() : null,
        smsOptInMethod: smsOptIn ? "website_checkbox_apply_page" : null,
        smsOptInDisclosureVersion: smsOptIn ? SMS_DISCLOSURE_VERSION : null,
        smsOptInDisclosureText: smsOptIn ? SMS_OPT_IN_DISCLOSURE : null,
        smsOptInSourcePath: smsOptIn ? "/apply" : null,

        submittedAt: serverTimestamp(),
      });

      setSubmitted(true);
    } catch (error) {
      console.error("Error submitting application:", error);
      alert("Failed to submit application. Please try again.");
    }

    setLoading(false);
  };

  if (submitted) {
    return (
      <div className="relative min-h-screen flex items-center justify-center bg-black text-white overflow-hidden">
        <img
          src={hero}
          alt="Poker table"
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-black/70" />

        <div className="relative z-10">
          <div className="bg-zinc-900/95 backdrop-blur-sm p-8 rounded-lg border border-zinc-800 text-center max-w-lg">
            <h2 className="text-2xl font-semibold text-[#B87333] mb-4">
              Application Submitted
            </h2>
            <p className="text-zinc-200">
              Thank you! Your application is pending admin review.
            </p>
            <p className="text-zinc-400 text-sm mt-3">
              If approved, we will contact you using the information provided in your application.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-black text-white overflow-hidden">
      <img
        src={hero}
        alt="Poker table"
        className="absolute inset-0 w-full h-full object-cover opacity-40"
      />

      <div className="absolute inset-0 bg-black/70" />

      <div className="absolute inset-0 opacity-30 pointer-events-none">
        <div className="absolute -top-40 -left-40 h-[520px] w-[520px] rounded-full blur-3xl bg-[#B87333]/20" />
        <div className="absolute -bottom-52 -right-52 h-[620px] w-[620px] rounded-full blur-3xl bg-[#B87333]/15" />
      </div>

      <div className="relative z-10 max-w-3xl mx-auto px-6 py-10">
        <form
          onSubmit={handleSubmit}
          className="bg-zinc-900/95 backdrop-blur-sm p-8 rounded-lg border border-zinc-800 space-y-5"
        >
          <div className="flex items-center justify-between">
            <Link to="/" className="text-zinc-400 hover:text-zinc-200 text-sm">
              ← Back to Home
            </Link>

            <Link
              to="/login"
              className="text-sm font-semibold text-zinc-950 px-3 py-2 rounded bg-[#B87333] hover:bg-[#A46230]"
            >
              Login
            </Link>
          </div>

          <img
            src={logo}
            alt="River Ranch Poker Club"
            className="h-34 mb-2 mx-auto"
          />

          <div className="text-center">
            <h2 className="text-2xl font-semibold text-[#B87333]">
              Membership Application
            </h2>
            <p className="text-zinc-400 text-sm mt-2 max-w-xl mx-auto">
              River Ranch Poker Club is a private members club. Please review the
              membership agreement, club rules, and messaging disclosures before
              submitting your application.
            </p>
          </div>

          <div className="grid gap-4">
            <input
              type="text"
              placeholder="Full Name"
              className="w-full p-3 bg-zinc-800 text-zinc-100 rounded"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />

            <div>
              <input
                type="email"
                placeholder="Email"
                className={`w-full p-3 rounded text-zinc-100 ${
                  emailError ? "bg-red-600" : "bg-zinc-800"
                }`}
                value={email}
                onChange={handleEmailChange}
                required
              />
              {emailError ? (
                <p className="text-red-400 text-sm mt-1">{emailError}</p>
              ) : null}
            </div>

            <div>
              <input
                type="tel"
                placeholder="Phone Number"
                className={`w-full p-3 rounded text-zinc-100 ${
                  phoneError ? "bg-red-600" : "bg-zinc-800"
                }`}
                value={phone}
                onChange={handlePhoneChange}
                required
              />
              {phoneError ? (
                <p className="text-red-400 text-sm mt-1">{phoneError}</p>
              ) : (
                <p className="text-zinc-500 text-xs mt-1">
                  Required for membership account communications and optional text message notifications.
                </p>
              )}
            </div>

            <div>
              <label className="block text-zinc-100 font-semibold mb-2">
                Date of Birth
              </label>
              <input
                type="date"
                className={`w-full p-3 rounded text-zinc-100 ${
                  underage ? "bg-red-600" : "bg-zinc-800"
                }`}
                value={dob}
                onChange={handleDobChange}
                required
              />
              {underage ? (
                <p className="text-red-400 text-sm mt-1">
                  Applicants must be 21 years or older.
                </p>
              ) : null}
            </div>
          </div>

          <div className="pt-2">
            <h3 className="text-lg font-semibold text-[#B87333] mb-3">
              Membership Agreement & Club Rules
            </h3>

            <div className="rounded-lg border border-zinc-800 bg-zinc-950/80 p-4 max-h-72 overflow-y-auto text-sm leading-relaxed text-zinc-300 space-y-4">
              <div>
                <p className="font-semibold text-zinc-100">
                  1. Private Members Club
                </p>
                <p className="mt-1">
                  River Ranch Poker Club operates as a private social club for
                  members. Participation in club activities, including poker
                  games and tournaments, is limited to approved members and
                  invited guests when permitted by the club.
                </p>
              </div>

              <div>
                <p className="font-semibold text-zinc-100">
                  2. Nature of Poker Activities
                </p>
                <p className="mt-1">
                  River Ranch Poker Club hosts poker games and tournaments as
                  social activities for its members. All poker games are played
                  between members, and participation is voluntary.
                </p>
              </div>

              <div>
                <p className="font-semibold text-zinc-100">
                  3. No Profit From Poker Games
                </p>
                <p className="mt-1">
                  River Ranch Poker Club does not take a rake or profit from the
                  poker games it hosts. The club does not collect a percentage
                  of pots, winnings, or tournament prize pools.
                </p>
                <p className="mt-1">
                  Membership fees, seat fees, and similar charges support club
                  operations and administration, including facilities,
                  equipment, staffing, food, software, and event management.
                  These fees are not tied to game outcomes or player winnings.
                </p>
              </div>

              <div>
                <p className="font-semibold text-zinc-100">
                  4. Membership Approval
                </p>
                <p className="mt-1">
                  Submission of an application does not guarantee membership.
                  Applications are reviewed and approved at the discretion of
                  club management.
                </p>
              </div>

              <div>
                <p className="font-semibold text-zinc-100">
                  5. Club Conduct
                </p>
                <p className="mt-1">
                  Members agree to conduct themselves in a respectful and
                  sportsmanlike manner. The club may suspend or revoke
                  membership for cheating, collusion, harassment, disruptive
                  behavior, or other conduct deemed harmful to the club or its
                  members.
                </p>
              </div>

              <div>
                <p className="font-semibold text-zinc-100">
                  6. Tournament & Game Rules
                </p>
                <p className="mt-1">
                  Members agree to follow all posted house rules, tournament
                  rules, and club policies. River Ranch Poker Club reserves the
                  right to enforce rules and resolve disputes.
                </p>
              </div>

              <div>
                <p className="font-semibold text-zinc-100">
                  7. No Guarantee of Games
                </p>
                <p className="mt-1">
                  The club does not guarantee the availability of games,
                  tournaments, or events on any particular date.
                </p>
              </div>

              <div>
                <p className="font-semibold text-zinc-100">
                  8. Assumption of Risk
                </p>
                <p className="mt-1">
                  Members voluntarily participate in club activities at their
                  own risk. River Ranch Poker Club, its organizers, and staff
                  are not responsible for personal loss, injury, or damages that
                  may occur while participating in club activities.
                </p>
              </div>

              <div>
                <p className="font-semibold text-zinc-100">
                  9. Membership Revocation
                </p>
                <p className="mt-1">
                  Membership may be suspended or revoked at any time for
                  violations of club rules or conduct deemed detrimental to the
                  club or its members.
                </p>
              </div>

              <div>
                <p className="font-semibold text-zinc-100">
                  10. Age Requirement
                </p>
                <p className="mt-1">
                  Membership is limited to individuals 21 years of age or older.
                  By submitting an application, the applicant confirms that they
                  meet this requirement.
                </p>
              </div>
            </div>

            <label className="mt-4 flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                className="mt-1 h-4 w-4 accent-[#B87333]"
                checked={agreementAccepted}
                onChange={(e) => setAgreementAccepted(e.target.checked)}
                required
              />
              <span className="text-sm text-zinc-300 leading-relaxed">
                I confirm that I have read and agree to the River Ranch Poker
                Club Membership Agreement and Club Rules.
              </span>
            </label>

            {!agreementAccepted ? (
              <p className="text-zinc-500 text-xs mt-2">
                Agreement acceptance is required before submitting your
                application.
              </p>
            ) : null}
          </div>

          <div className="pt-2">
            <h3 className="text-lg font-semibold text-[#B87333] mb-3">
              Optional Text Message Notifications
            </h3>

            <div className="rounded-lg border border-zinc-800 bg-zinc-950/80 p-4 space-y-4">
              <p className="text-sm text-zinc-300 leading-relaxed">
                Members may optionally consent to receive recurring automated text
                messages from River Ranch Poker Club related to tournament
                announcements, event reminders, registration confirmations, and
                membership account communications.
              </p>

              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4 accent-[#B87333]"
                  checked={smsOptIn}
                  onChange={(e) => setSmsOptIn(e.target.checked)}
                />
                <span className="text-sm text-zinc-300 leading-relaxed">
                  By checking this box, I agree to receive recurring automated
                  text messages from River Ranch Poker Club regarding tournament
                  announcements, event reminders, registration confirmations, and
                  membership account communications. Message frequency varies.
                  Message and data rates may apply. Reply STOP to opt out. Reply
                  HELP for help. Consent is not a condition of membership or
                  purchase.
                </span>
              </label>

              <div className="text-xs text-zinc-500 leading-relaxed space-y-2">
                <p>
                  This consent is optional and is not required to submit a membership
                  application.
                </p>
                <p>
                  By opting in, you agree to our{" "}
                  <Link
                    to="/privacy"
                    className="text-[#B87333] hover:text-[#d08a49] underline underline-offset-2"
                  >
                    Privacy Policy
                  </Link>{" "}
                  and{" "}
                  <Link
                    to="/terms"
                    className="text-[#B87333] hover:text-[#d08a49] underline underline-offset-2"
                  >
                    Terms of Service
                  </Link>
                  .
                </p>
              </div>
            </div>
          </div>

          <button
            type="submit"
            className="w-full py-3 bg-[#B87333] hover:bg-[#A46230] text-zinc-950 font-semibold rounded disabled:opacity-60 disabled:cursor-not-allowed"
            disabled={
              loading ||
              underage ||
              !!emailError ||
              !!phoneError ||
              !agreementAccepted
            }
          >
            {loading ? "Submitting..." : "Submit Application"}
          </button>
        </form>
      </div>
    </div>
  );
}